import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppData } from "../context/AppDataContext";
import { filterToursForPublic } from "../constants/publicTours";
import { getTourCardCopy } from "../utils/tourCardCopy";
import { sortToursForDisplay, splitFeaturedTours } from "../utils/tourSort";

const categories = [
  { id: "all", key: "tours.filterAll" },
  { id: "beach", key: "tours.filterBeach" },
  { id: "city", key: "tours.filterCity" },
  { id: "adventure", key: "tours.filterAdventure" },
  { id: "romantic", key: "tours.filterRomantic" },
];

function formatDate(dateStr, lng) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(lng || "hr-HR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Tours() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState("all");
  const { tours: currentTours } = useAppData();

  const publicTours = filterToursForPublic(currentTours);
  const filtered =
    active === "all"
      ? publicTours
      : publicTours.filter((tour) => tour.category === active);

  const sorted = sortToursForDisplay(filtered);
  const showFeatured = active === "all";
  const { featured, rest } = showFeatured
    ? splitFeaturedTours(sorted)
    : { featured: [], rest: sorted };

  const renderTourCard = (tour) => {
    const lng = i18n.language || "hr";
    const base = lng.slice(0, 2);
    const { title, desc } = getTourCardCopy(tour, base, t);
    return (
      <article
        className="tour-card"
        key={tour.id}
        onClick={() => navigate(`/tour/${tour.key}`)}
        style={{ cursor: "pointer" }}
      >
        <div
          className="tour-image"
          style={{ backgroundImage: `url(${tour.image})` }}
        >
          <div className="tour-tag">{tour.tag}</div>
        </div>
        <div className="tour-body">
          <h3>{title}</h3>
          <div className="tour-meta">
            <span>📍 {tour.country}</span>
            <span className="tour-meta-date">
              🗓 {formatDate(tour.date, lng)}
            </span>
            <span>
              ⏳ {tour.days} d / {tour.nights} {t("tours.nights")}
            </span>
          </div>
          <p className="tour-description">{desc}</p>
          <div className="tour-footer">
            <div className="tour-price">
              <span className="tour-price-main">
                {t("tours.from")} {tour.price} €
              </span>
              <span className="tour-price-note">
                {t("tours.perPerson")}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-cta-gold"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/tour/${tour.key}`);
              }}
            >
              {t("tours.btnDetails")}
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section id="tours" className="section">
      <div className="container">
        <header className="section-header">
          <h2>{t("tours.sectionTitle")}</h2>
          <p>{t("tours.sectionSubtitle")}</p>
        </header>

        <div className="filters">
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`filter-btn ${active === cat.id ? "active" : ""}`}
              onClick={() => setActive(cat.id)}
            >
              {t(cat.key)}
            </button>
          ))}
        </div>

        {sorted.length === 0 ? (
          <p className="no-tours">{t("tours.noTours")}</p>
        ) : (
          <div className="tours-layout">
            {featured.length > 0 ? (
              <div className="tours-grid tours-grid--top">
                {featured.map(renderTourCard)}
              </div>
            ) : null}
            {rest.length > 0 ? (
              <div className="tours-grid">
                {rest.map(renderTourCard)}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
